import { type Vec2, vec } from '../math';

import { type EntitySource } from './source';

/**
 * Moving, turning and resizing a whole selection.
 *
 * ## Why this transforms *sources*, and only some of them
 *
 * In a constructive DAG almost nothing has coordinates of its own. A triangle's sides are
 * derived from its corners; its circumcircle is derived from the sides. "Move the triangle"
 * therefore cannot mean "assign new coordinates to eleven entities" — most of them have no
 * coordinates to assign, and writing some would contradict the constructions that produce
 * them.
 *
 * It means: move the entities that *do* carry coordinates, and let evaluation carry the rest.
 * Everything derived follows for free, exactly as it does when a single point is dragged, and
 * every construction in the figure stays true afterwards. A circumcircle moved this way is
 * still the circumcircle.
 *
 * So `transformSource` returns `null` for a derived source. `null` here does not mean failure;
 * it means *nothing to do, this one moves by itself*.
 *
 * ## Why a similarity and not an arbitrary matrix
 *
 * A general affine map can shear and can scale differently along each axis. Under one, a
 * circle becomes an ellipse and a square becomes a rhombus — and the *source* has nowhere to
 * put that: `free-ellipse` stores `rx`, `ry` and a rotation, which cannot express a sheared
 * ellipse at all. The transform would have to silently approximate, and a geometry editor that
 * silently approximates is the thing this whole codebase is arranged to avoid.
 *
 * A similarity — turn, uniform resize, shift — maps every shape to one of its own kind. Every
 * free source can represent its own image exactly, so the operation is closed and lossless.
 * Non-uniform scaling is genuinely absent rather than badly done.
 */

export interface Similarity {
  /** The fixed point of the rotation and the scaling. */
  readonly center: Vec2;
  /** Radians, counter-clockwise. */
  readonly rotation: number;
  /** Uniform. `1` leaves size alone. */
  readonly scale: number;
  /** Applied after the rotation and scaling. */
  readonly translate: Vec2;
}

export const IDENTITY_SIMILARITY: Similarity = {
  center: vec.vec2(0, 0),
  rotation: 0,
  scale: 1,
  translate: vec.vec2(0, 0),
};

/** A pure move, which is the overwhelmingly common case. */
export function translation(by: Vec2): Similarity {
  return { ...IDENTITY_SIMILARITY, translate: by };
}

/** Where `point` ends up. */
export function applySimilarity(transform: Similarity, point: Vec2): Vec2 {
  const { center, rotation, scale, translate } = transform;

  const offset = vec.subtract(point, center);
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);

  const turned = vec.vec2(
    (offset.x * cos - offset.y * sin) * scale,
    (offset.x * sin + offset.y * cos) * scale
  );

  return vec.add(vec.add(center, turned), translate);
}

/**
 * The source after the transform, or `null` if it has no coordinates of its own.
 *
 * The exhaustive switch is the point of writing it this way: a construction added later
 * cannot be forgotten here, because the compiler will not accept the file until its case
 * exists and somebody has decided which of the two answers it gives.
 */
export function transformSource(source: EntitySource, transform: Similarity): EntitySource | null {
  const at = (point: Vec2) => applySimilarity(transform, point);

  switch (source.op) {
    /*
     * Derived, so nothing to rewrite: a plane has no coordinates of its own, and its section is
     * re-cut from the solid every time the solid moves.
     */
    case 'plane':
    case 'plane-parallel':
    case 'plane-turn':
    case 'plane-free':
    case 'plane-point':
    case 'space':
    case 'dihedral-mark':
    case 'plane-meet':
    case 'plane-foot':
      return null;

    case 'free-point':
      return { ...source, position: at(source.position) };

    case 'free-polyline':
      return { ...source, points: source.points.map(at) };

    case 'free-ellipse':
      /*
       * Radii scale, the rotation turns, the centre moves. Exactly representable — which is
       * the whole reason the transform is restricted to similarities.
       */
      return {
        ...source,
        center: at(source.center),
        rx: source.rx * transform.scale,
        ry: source.ry * transform.scale,
        rotation: source.rotation + transform.rotation,
      };

    case 'image':
      return {
        ...source,
        position: at(source.position),
        width: source.width * transform.scale,
        height: source.height * transform.scale,
      };

    /*
     * The corner moves; the table does not turn or resize with the figure.
     *
     * A rotated variation table is unreadable, and a scaled one puts its numbers at a size
     * that has nothing to do with every other number on the page. It is a caption, and a
     * caption stays upright — so only the translation is taken.
     */
    case 'variation-table':
      return { ...source, at: at(source.at) };

    /*
     * An *unanchored* label carries its own position, in `offset`, and moves. An anchored one
     * does not: its offset is relative to an entity that is itself moving, so transforming it
     * as well would move the label twice.
     *
     * The offset is only turned and scaled, never translated — a translation is already
     * carried by the point the offset is measured from.
     */
    case 'text':
      return source.anchor === null
        ? { ...source, offset: at(source.offset) }
        : { ...source, offset: rotateAndScale(source.offset, transform) };

    case 'measurement':
      return { ...source, offset: rotateAndScale(source.offset, transform) };

    /*
     * Derived in shape but parameterised by a length.
     *
     * These take their position from entities that are already moving, so nothing here is
     * translated — but each stores a size in world units, and "make this figure twice as big"
     * has to mean that too. A circle whose radius the user typed as 5 staying at 5 while the
     * triangle around it doubles is not a faithful scaling, it is a broken drawing.
     */
    case 'circle-radius':
      return { ...source, radius: source.radius * transform.scale };

    case 'ellipse':
      return {
        ...source,
        rx: source.rx * transform.scale,
        ry: source.ry * transform.scale,
        rotation: source.rotation + transform.rotation,
      };

    case 'solid':
      return {
        ...source,
        width: source.width * transform.scale,
        height: source.height * transform.scale,
        depth: source.depth * transform.scale,
      };

    /*
     * Everything below is derived outright. It has no coordinates and no size of its own, and
     * needs none: it is recomputed from parents that have moved, so it follows by itself and
     * stays exactly as true as it was before.
     *
     * `polyhedron` is here for the plainest version of that reason: it is the hull of points that
     * are themselves moving, so it follows them and has nothing of its own to transform.
     *
     * `function-graph` is in the run for a different reason, and it is worth stating: it is
     * defined by a formula and a range, and a similarity cannot be expressed in either. Moving
     * `y = x^2` by three is `y = (x-3)^2` — a *different formula* — and rewriting a user's
     * algebra behind their back is not something a teaching tool should do. It stays put,
     * visibly, and the user edits it.
     */
    case 'segment':
    case 'ray':
    case 'line':
    case 'vector':
    case 'solid-figure':
    case 'solid-vertex':
    case 'solid-rim-point':
    case 'solid-foot':
    case 'midpoint':
    case 'ratio-point':
    case 'segment-midpoint':
    case 'circle-center':
    case 'circle-through':
    case 'circle-3-points':
    case 'arc-3-points':
    case 'arc-center':
    case 'ellipse-foci':
    case 'perpendicular-line':
    case 'perpendicular-bisector':
    case 'parallel-line':
    case 'angle-bisector':
    case 'tangent-line':
    case 'intersection':
    case 'projected-point':
    case 'circumcenter':
    case 'incenter':
    case 'centroid':
    case 'orthocenter':
    case 'polygon':
    case 'regular-polygon':
    case 'reflected-point':
    case 'rotated-point':
    case 'translated-point':
    case 'scaled-point':
    case 'locus-point':
    case 'polygon-boolean':
    case 'function-graph':
    case 'graph-tangent':
    case 'graph-marks':
    case 'polyhedron':
    case 'solid-cut':
    case 'axes':
      return null;

    /*
     * The mark has nothing to transform — it is three points and a rule, and the points are
     * transformed on their own account. The one exception is a number the user has dragged: its
     * offset is a displacement in the drawing, so turning the figure has to turn it too, or the
     * number carefully placed below a vertex ends up above it.
     *
     * `null` when there is no such offset, which keeps every existing figure on exactly the
     * path it was on before.
     */
    case 'angle-mark':
      return source.valueOffset === undefined
        ? null
        : { ...source, valueOffset: rotateAndScale(source.valueOffset, transform) };
  }
}

/**
 * A displacement turned and resized, but not moved.
 *
 * A label's offset is a vector from its anchor, not a position. Translating it would move the
 * label a second time, on top of the anchor it is already following.
 */
function rotateAndScale(offset: Vec2, transform: Similarity): Vec2 {
  return applySimilarity(
    { ...transform, center: vec.vec2(0, 0), translate: vec.vec2(0, 0) },
    offset
  );
}
